import React, { useState } from 'react';
import { Box, FormControl, MenuItem, Select, Typography, Popover, SelectChangeEvent } from '@mui/material';
import { FilterComponentProps } from './UserProfileFilters';
import { DateRangeFilter } from '@/pages/user/types';
import { IconOccasion } from '@/icons';
import BaseFilterPill from './BaseFilterPill';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';

interface DateFilterProps extends FilterComponentProps {
  value: DateRangeFilter | undefined;
  onChange: (value: DateRangeFilter) => void;
  label?: string; // Custom label for different date filters (dateAdded, dateLastActive)
}

type DateFilterMode = 'before' | 'after' | 'on';

const DateFilter: React.FC<DateFilterProps> = ({ 
  value = {}, 
  onChange, 
  onRemove, 
  label = "Date" 
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [mode, setMode] = useState<DateFilterMode>('on');
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(
    value.from ? dayjs(value.from) : (value.to ? dayjs(value.to) : null)
  );

  // Initialize mode based on from/to values
  React.useEffect(() => {
    if (value.from && value.to) {
      setMode('on');
      setSelectedDate(dayjs(value.from));
    } else if (value.from) {
      setMode('after');
      setSelectedDate(dayjs(value.from));
    } else if (value.to) {
      setMode('before');
      setSelectedDate(dayjs(value.to));
    }
  }, []);

  const handleOpenPicker = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePicker = () => {
    setAnchorEl(null);
  };

  const handleDateChange = (date: Dayjs | null) => {
    setSelectedDate(date);
    if (!date) return;

    let newValue: DateRangeFilter = {};
    
    switch (mode) {
      case 'before':
        newValue = { to: date.toDate() };
        break;
      case 'after':
        newValue = { from: date.toDate() };
        break;
      case 'on':
        // For exact date, set from to start of day and to to end of day
        newValue = { 
          from: date.startOf('day').toDate(),
          to: date.endOf('day').toDate() 
        };
        break;
    }
    
    onChange(newValue);
  };

  const handleModeChange = (event: SelectChangeEvent<DateFilterMode>) => {
    const newMode = event.target.value as DateFilterMode;
    setMode(newMode);
    
    // Update filter value based on new mode
    if (selectedDate) {
      let newValue: DateRangeFilter = {};
      
      switch (newMode) {
        case 'before':
          newValue = { to: selectedDate.toDate() };
          break;
        case 'after':
          newValue = { from: selectedDate.toDate() };
          break;
        case 'on':
          newValue = { 
            from: selectedDate.startOf('day').toDate(),
            to: selectedDate.endOf('day').toDate() 
          };
          break;
      }
      
      onChange(newValue);
    }
  };

  const getDisplayText = () => {
    if (!selectedDate) return 'Select date';
    
    const formattedDate = selectedDate.format('MMM D, YYYY');
    
    switch (mode) {
      case 'before':
        return `Before ${formattedDate}`;
      case 'after':
        return `After ${formattedDate}`;
      case 'on':
        return `On ${formattedDate}`;
      default:
        return 'Select date';
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <BaseFilterPill
        icon={<IconOccasion fontSize="small" />}
        label={label}
        onRemove={onRemove}
        background="#fff0e1"
      >
        <Box 
          sx={{ 
            cursor: 'pointer', 
            mr: 2,
            display: 'flex',
            alignItems: 'center'
          }} 
          onClick={handleOpenPicker}
        >
          <Typography variant="body2" color="text.secondary">
            {getDisplayText()}
          </Typography>
        </Box>
      </BaseFilterPill>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePicker}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <Select
              value={mode}
              onChange={handleModeChange}
              displayEmpty
            >
              <MenuItem value="before">Before</MenuItem>
              <MenuItem value="after">After</MenuItem>
              <MenuItem value="on">On</MenuItem>
            </Select>
          </FormControl>

          <DatePicker
            label="Select date"
            value={selectedDate}
            onChange={handleDateChange}
            slotProps={{
              textField: { size: 'small', fullWidth: true }
            }}
          />
        </Box>
      </Popover>
    </>
  );
};

export default DateFilter; 